/**
 * ActionsPage — Action items and risk register.
 * Shows auto-generated and user-created actions with status management.
 */

import React, { useState, useMemo } from 'react';
import { Card, CardContent } from '../../components/ui';
import { useActions, useActionSummary, useUpdateAction } from './api';
import { formatCurrency, priorityBg, sortData } from './utils';
import { cn } from '../../shared/lib/cn';
import { SortableHeader } from './SortableHeader';
import type { ActionStatus, ActionPriority } from './types';

const STATUSES: ActionStatus[] = ['open', 'in_progress', 'escalated', 'resolved', 'closed'];
const PRIORITIES: ActionPriority[] = ['critical', 'high', 'medium', 'low'];

export default function ActionsPage() {
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [priorityFilter, setPriorityFilter] = useState<string>('');
  const [sortBy, setSortBy] = useState('priority');
  const [sortDir, setSortDir] = useState('ASC');

  const { data: actions, isLoading } = useActions({
    status: statusFilter || undefined,
    priority: priorityFilter || undefined,
  });
  const { data: summary } = useActionSummary();
  const updateAction = useUpdateAction();

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortDir(d => d === 'DESC' ? 'ASC' : 'DESC');
    } else {
      setSortBy(field);
      setSortDir('ASC');
    }
  };

  const sortedActions = useMemo(
    () => sortData(actions ?? [], sortBy, sortDir),
    [actions, sortBy, sortDir],
  );

  return (
    <div className="space-y-6 mt-2">
      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
          {[
            { label: 'Open', value: summary.total_open, accent: summary.total_open > 0 ? 'text-red-600' : '' },
            { label: 'In Progress', value: summary.total_in_progress },
            { label: 'Escalated', value: summary.total_escalated, accent: summary.total_escalated > 0 ? 'text-orange-600' : '' },
            { label: 'Resolved', value: summary.total_resolved, accent: 'text-emerald-600' },
            { label: 'Overdue', value: summary.total_overdue, accent: summary.total_overdue > 0 ? 'text-red-600' : '' },
            { label: 'Realized Impact', value: formatCurrency(summary.realized_cash_impact, true), accent: 'text-emerald-600' },
          ].map(kpi => (
            <Card key={kpi.label}>
              <CardContent className="p-3">
                <p className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400">{kpi.label}</p>
                <p className={cn('text-lg font-bold', kpi.accent || 'text-charcoal dark:text-gray-100')}>
                  {typeof kpi.value === 'number' ? kpi.value : kpi.value}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          className="text-sm border border-gray-200 dark:border-dark-border rounded-lg px-3 py-1.5 bg-white dark:bg-dark-surface"
        >
          <option value="">All Statuses</option>
          {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select
          value={priorityFilter}
          onChange={e => setPriorityFilter(e.target.value)}
          className="text-sm border border-gray-200 dark:border-dark-border rounded-lg px-3 py-1.5 bg-white dark:bg-dark-surface"
        >
          <option value="">All Priorities</option>
          {PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
        </select>
      </div>

      {/* Actions Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 dark:border-dark-border bg-gray-50 dark:bg-dark-surface-2">
                  <SortableHeader label="Priority" field="priority" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                  <SortableHeader label="Type" field="action_type" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                  <SortableHeader label="Description" field="description" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                  <SortableHeader label="Impact" field="cash_impact_estimated" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} align="right" />
                  <SortableHeader label="Due Date" field="due_date" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                  <SortableHeader label="Status" field="status" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-dark-border/50">
                {isLoading ? (
                  <tr><td colSpan={6} className="text-center py-8 text-gray-400">Loading actions...</td></tr>
                ) : !sortedActions.length ? (
                  <tr><td colSpan={6} className="text-center py-8 text-gray-500">No actions found for the selected filters.</td></tr>
                ) : sortedActions.map(action => (
                  <tr key={action.id} className="hover:bg-gray-50 dark:hover:bg-dark-surface-2/50">
                    <td className="py-2 px-3">
                      <span className={cn('px-2 py-0.5 rounded text-xs font-medium', priorityBg(action.priority))}>
                        {action.priority}
                      </span>
                    </td>
                    <td className="py-2 px-3 text-gray-600 dark:text-gray-400 text-xs whitespace-nowrap">{action.action_type}</td>
                    <td className="py-2 px-3 text-charcoal dark:text-gray-200" style={{ maxWidth: '400px' }}>
                      {action.description}
                    </td>
                    <td className="py-2 px-3 text-right tabular-nums font-medium text-charcoal dark:text-gray-200 whitespace-nowrap">
                      {action.cash_impact_estimated != null ? formatCurrency(action.cash_impact_estimated, true) : '—'}
                    </td>
                    <td className="py-2 px-3 text-gray-500 dark:text-gray-400 text-xs whitespace-nowrap">{action.due_date || '—'}</td>
                    <td className="py-2 px-3">
                      <select
                        value={action.status}
                        onChange={e => updateAction.mutate({ id: action.id, status: e.target.value as ActionStatus })}
                        className="text-xs border border-gray-200 dark:border-dark-border rounded px-2 py-1 bg-white dark:bg-dark-surface"
                      >
                        {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
